import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Link2Off, Lock, NotebookText, PenLine } from 'lucide-react';
import { API } from '../lib/api';
import BrandLogo from '../components/BrandLogo';
import { ThemeToggleButton } from '../components/ThemeToggle';
import { DamageScore, EvidenceMarker, ProvenanceChip, SeverityChip } from '../components/evidence';
import { VIEW_LABELS_MAP, damageProfile, formatType, money, toNumber } from '../lib/damageCopy';

function formatDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

function findingCost(f) {
  return f.cost ?? damageProfile(f).defaultCost;
}

// Keep shared reports out of search engines and history-sharing previews.
function useNoIndex(title) {
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    document.title = title;
    return () => {
      document.title = prevTitle;
      meta.remove();
    };
  }, [title]);
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-[1080px] items-center gap-2.5 px-4 py-3 md:px-6">
          <BrandLogo className="h-[30px] w-[30px]" />
          <span className="font-display text-[19px] font-extrabold">RealSpect</span>
          <span className="ml-2 hidden text-sm font-bold text-ink-muted sm:inline">Shared inspection report</span>
          <ThemeToggleButton className="ml-auto" />
        </div>
      </header>
      <main className="mx-auto max-w-[1080px] px-4 py-6 md:px-6 md:py-8">{children}</main>
    </div>
  );
}

function FindingRow({ f, showPhoto }) {
  const profile = damageProfile(f);
  return (
    <li className="grid grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1.5 border-t border-line py-4 first:border-t-0" data-testid="shared-finding">
      <EvidenceMarker n={f.n} />
      <div className="min-w-0">
        <p className="m-0 font-bold">{profile.title}</p>
        <p className="m-0 text-sm text-ink-muted">
          {[f.location, showPhoto && `Photo ${f.photo_index}`].filter(Boolean).join(' · ')}
        </p>
      </div>
      <div className="text-right">
        <p className="m-0 font-mono text-[17px] font-bold tabular">{money(findingCost(f))}</p>
        <p className={`m-0 text-[13px] font-bold ${f.chargeable ? 'text-sev-high' : 'text-ink-muted'}`}>
          {f.chargeable ? 'Tenant charge' : 'Normal wear'}
        </p>
      </div>
      <p className="col-start-2 col-end-4 m-0 text-sm leading-[22px] text-ink-muted">{profile.description}</p>
      <div className="col-start-2 col-end-4 flex flex-wrap gap-1.5">
        {f.severity && <SeverityChip severity={f.severity} />}
        <ProvenanceChip source={f.edited ? 'ai_edited' : 'ai'} />
        {f.chargeable && (
          <span className="inline-flex h-6 items-center rounded-sm border border-line-strong px-2 text-[13px] font-bold">
            {f.reason_tag || profile.tag}
          </span>
        )}
      </div>
    </li>
  );
}

function RoomSection({ room }) {
  return (
    <section className="rounded-md border border-line bg-surface" data-testid="shared-room">
      <h2 className="m-0 border-b border-line px-5 py-4 font-display text-[22px] font-bold leading-7">{room.name}</h2>
      {room.views.map((view, vi) => {
        const multiPhoto = view.photos.length > 1;
        return (
          <div key={vi} className="border-b border-line p-5 last:border-b-0">
            <p className="m-0 mb-3 text-xs font-bold uppercase tracking-[0.08em] text-ink-muted">
              {VIEW_LABELS_MAP[view.view_label] || 'Overview'}
              {view.analyzed_at && ` · ${formatDate(view.analyzed_at)}`}
            </p>
            {view.photos.length > 0 && (
              <div className={`grid gap-3 ${multiPhoto ? 'sm:grid-cols-2' : ''}`}>
                {view.photos.map((p) => (
                  <figure key={p.photo_index} className="m-0 overflow-hidden rounded-md border border-line bg-sunken">
                    <img
                      src={`data:image/png;base64,${p.image_base64}`}
                      alt={`${room.name}, photo ${p.photo_index}, with numbered damage markers`}
                      className="block h-auto max-h-[480px] w-full object-contain"
                    />
                    {multiPhoto && <figcaption className="px-3 py-1.5 text-[13px] font-bold text-ink-muted">Photo {p.photo_index}</figcaption>}
                  </figure>
                ))}
              </div>
            )}
            {view.findings.length ? (
              <ul className="m-0 mt-2 list-none p-0">
                {view.findings.map((f) => <FindingRow key={f.n} f={f} showPhoto={multiPhoto} />)}
              </ul>
            ) : (
              <p className="m-0 mt-3 text-sm text-ink-muted">No damage recorded in this view.</p>
            )}
          </div>
        );
      })}
    </section>
  );
}

export default function SharedReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useNoIndex(report ? `${report.property_name} inspection report` : 'Inspection report');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/shared/${encodeURIComponent(token)}`)
      .then(({ data }) => { if (!cancelled) setReport(data); })
      .catch((err) => { if (!cancelled) setError(err.response?.status === 429 ? 'busy' : 'inactive'); });
    return () => { cancelled = true; };
  }, [token]);

  // Group photo views by room, in the order the rooms were inspected.
  const rooms = useMemo(() => {
    const byName = new Map();
    (report?.views || []).forEach((v) => {
      if (!byName.has(v.room_name)) byName.set(v.room_name, { name: v.room_name, views: [] });
      byName.get(v.room_name).views.push(v);
    });
    return [...byName.values()];
  }, [report]);

  const totals = useMemo(() => {
    let repair = 0;
    let tenant = 0;
    let count = 0;
    (report?.views || []).forEach((v) => v.findings.forEach((f) => {
      const c = toNumber(findingCost(f));
      repair += c;
      if (f.chargeable) tenant += c;
      count += 1;
    }));
    return { repair, tenant, count };
  }, [report]);

  if (error) {
    return (
      <Shell>
        <div className="mx-auto max-w-[520px] rounded-md border border-line bg-surface p-8 text-center" data-testid="shared-report-inactive">
          <Link2Off className="mx-auto h-8 w-8 text-ink-muted" strokeWidth={1.5} aria-hidden="true" />
          <h1 className="m-0 mt-3 font-display text-2xl font-bold">
            {error === 'busy' ? 'Too many requests' : "This link isn't active"}
          </h1>
          <p className="m-0 mt-2 text-ink-muted">
            {error === 'busy'
              ? 'Wait a minute, then reload the page.'
              : 'It may have been turned off or mistyped. Ask your landlord or property manager for a new link.'}
          </p>
        </div>
      </Shell>
    );
  }

  if (!report) {
    return (
      <Shell>
        <div className="animate-pulse space-y-4" aria-label="Loading report">
          <div className="h-36 rounded-md border border-line bg-sunken" />
          <div className="h-[420px] rounded-md border border-line bg-sunken" />
        </div>
      </Shell>
    );
  }

  const approved = report.status === 'completed';
  const manual = report.manual_findings || [];

  return (
    <Shell>
      <div className="space-y-6" data-testid="shared-report-page">
        <div className="flex flex-wrap items-start justify-between gap-6 rounded-md border border-line bg-surface p-6">
          <div className="min-w-0 flex-[1_1_320px]">
            <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">
              {formatType(report.inspection_type)} inspection
            </p>
            <h1 className="m-0 mt-1 font-display text-3xl font-bold leading-9 tracking-[-0.01em]" data-testid="shared-report-title">{report.property_name}</h1>
            <p className="m-0 mt-1 text-ink-muted">{report.property_address}</p>
            <p className="m-0 mt-1.5 font-mono text-sm font-semibold text-ink-muted">
              Inspected {formatDate(report.created_at)}{report.inspector_name && ` by ${report.inspector_name}`}
            </p>
            {approved ? (
              <p className="m-0 mt-3 inline-flex items-center gap-1.5 rounded-sm bg-sunken px-2 py-1 text-[13px] font-bold">
                <Lock className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" /> Final, approved {formatDate(report.completed_at)}
              </p>
            ) : (
              <p className="m-0 mt-3 inline-flex rounded-sm bg-marker-soft px-2 py-1 text-[13px] font-bold">
                Draft: your landlord may still change this report
              </p>
            )}
          </div>
          <DamageScore score={report.overall_score} />
        </div>

        <div className="grid gap-3 sm:grid-cols-3" data-testid="shared-totals">
          {[
            { label: 'Charged to tenant', value: money(totals.tenant), strong: true },
            { label: 'Total repair estimate', value: money(totals.repair) },
            { label: 'Items recorded', value: totals.count + manual.length },
          ].map((t) => (
            <div key={t.label} className={`rounded-md border p-4 ${t.strong ? 'border-ink bg-surface' : 'border-line bg-surface'}`}>
              <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">{t.label}</p>
              <p className="m-0 mt-1 font-mono text-[28px] font-bold leading-9 tabular">{t.value}</p>
            </div>
          ))}
        </div>
        <p className="m-0 -mt-3 text-[13px] text-ink-muted">
          Costs are repair estimates. Items marked normal wear are not charged to you.
        </p>

        {rooms.map((room) => <RoomSection key={room.name} room={room} />)}

        {manual.length > 0 && (
          <section className="rounded-md border border-line bg-surface p-5">
            <h2 className="m-0 font-display text-[22px] font-bold leading-7">Noted by the inspector</h2>
            <p className="m-0 mt-0.5 text-sm text-ink-muted">Things a photo can't show, such as odors or moisture readings.</p>
            <ul className="m-0 mt-2 list-none p-0">
              {manual.map((m, i) => (
                <li key={i} className="border-t border-line py-3 first:border-t-0">
                  <p className="m-0 font-bold">{formatType(m.finding_type)} <span className="font-normal text-ink-muted">· {m.room_name}{m.location && `, ${m.location}`}</span></p>
                  {m.description && <p className="m-0 mt-1 text-sm leading-[22px] text-ink-muted">{m.description}</p>}
                  {m.evidence_note && <p className="m-0 mt-1 text-sm leading-[22px]"><PenLine className="mr-1 inline h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />{m.evidence_note}</p>}
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {m.severity && <SeverityChip severity={m.severity} />}
                    <ProvenanceChip source="manual" />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {report.notes?.trim() && (
          <section className="rounded-md border border-line bg-surface p-5">
            <h2 className="m-0 flex items-center gap-2 font-display text-[22px] font-bold leading-7">
              <NotebookText className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" /> Inspector notes
            </h2>
            <p className="m-0 mt-2 whitespace-pre-wrap leading-[24px]">{report.notes}</p>
          </section>
        )}

        <p className="m-0 border-t border-line pt-4 text-[13px] text-ink-muted">
          Read-only report shared through RealSpect. Questions about a charge? Contact your landlord or property manager directly.
        </p>
      </div>
    </Shell>
  );
}
